import { safeSessionStorage } from "@/utils/safeStorage";
import { ComponentType, lazy } from 'react';

/**
 * React.lazy with automatic retry + cache-bust for transient chunk failures.
 *
 * Dynamic imports fail on flaky networks, mid-deploy chunk rotation, or CDN
 * 503s. Plain React.lazy caches the rejected promise, so the route can never
 * recover and the page is stuck on its Suspense fallback forever. This retries
 * with backoff, then falls back to a hard reload once (guarded by
 * sessionStorage so we never loop).
 */
const RELOAD_FLAG = '__gb_lazy_reload';

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  { retries = 2, delayMs = 400 }: { retries?: number; delayMs?: number } = {},
) {
  return lazy(async () => {
    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await factory();
      } catch (error) {
        lastError = error;
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
    }

    // Last resort: a stale index.html / service-worker cache. Reload once.
    try {
      if (typeof sessionStorage !== 'undefined' && !safeSessionStorage.getItem(RELOAD_FLAG)) {
        safeSessionStorage.setItem(RELOAD_FLAG, '1');
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        window.location.reload();
      }
    } catch {
      /* ignore */
    }

    throw lastError;
  });
}
